import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ScheduleItem } from '@/components/SchedulerTypes';
import { useToast } from '@/hooks/use-toast';

interface CopyPasteContextType {
  copiedItem: ScheduleItem | null;
  copyScheduleItem: (item: ScheduleItem) => void;
  clearClipboard: () => void;
  hasCopiedItem: boolean;
}

const CopyPasteContext = createContext<CopyPasteContextType | undefined>(undefined);

export const useCopyPaste = () => {
  const context = useContext(CopyPasteContext);
  if (context === undefined) {
    throw new Error('useCopyPaste must be used within a CopyPasteProvider');
  }
  return context;
};

interface CopyPasteProviderProps {
  children: ReactNode;
}

export const CopyPasteProvider: React.FC<CopyPasteProviderProps> = ({ children }) => {
  const [copiedItem, setCopiedItem] = useState<ScheduleItem | null>(null);
  const { toast } = useToast();

  const copyScheduleItem = (item: ScheduleItem) => {
    setCopiedItem(item);
    toast({
      title: "Copied",
      description: `Schedule item "${item.title}" copied to clipboard`,
    });
  };

  const clearClipboard = () => {
    setCopiedItem(null);
  };

  const hasCopiedItem = copiedItem !== null;

  return (
    <CopyPasteContext.Provider 
      value={{ 
        copiedItem, 
        copyScheduleItem, 
        clearClipboard, 
        hasCopiedItem 
      }}
    >
      {children}
    </CopyPasteContext.Provider>
  );
};